import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Recover a virtualized scroll container from the iOS flex-min-size break
 * (rule #39 bug-2) that can re-occur mid-session on any re-layout, freezing the
 * scroll. The break: iOS Safari unreliably resolves a `flex: 1` scroll
 * container's `min-height: auto` zeroing when its child carries a huge content
 * height (a `min-height`/`height` of thousands of px reserving the full scroll
 * range), so the container grows to its content height instead of its flex
 * slot, `overflow-y: auto` has nothing to scroll, and the view is dead until the
 * overlay is remounted (Back + re-enter). The `min-height: 0` CSS cure handles
 * the cold-mount case; this hook handles a mid-session re-break.
 *
 * Trigger-agnostic by design: a ResizeObserver watches the scroll container and
 * its fixed parent, so the broken detector re-checks after ANY re-layout (store
 * changes from reconnect enrichment/sync, layout/filter/search/text-scale
 * changes, viewport changes). The flex break itself resizes the container (it
 * grows to content height), which the observer catches directly. `online` and
 * `visibilitychange` are belt-and-suspenders for a foreground that re-breaks
 * without an intervening resize.
 *
 * Returns a remount key the host applies as `key={recoverKey}` to the scroll
 * container; the keyed remount is the same cure as Back + re-enter. Fires ONLY
 * when genuinely frozen, so healthy scrolling and state are never disturbed.
 *
 * @param scrollRef the `overflow-y: auto` scroll container element
 * @param boundsRef the `position: fixed; inset: 0` overlay root it lives in
 */
export function useScrollFreezeRecovery(
  scrollRef: RefObject<HTMLElement | null>,
  boundsRef: RefObject<HTMLElement | null>,
  beforeRecover?: () => void,
): number {
  const [recoverKey, setRecoverKey] = useState(0);
  const lastRecoverRef = useRef(0);
  const beforeRecoverRef = useRef(beforeRecover);
  useEffect(() => {
    beforeRecoverRef.current = beforeRecover;
  }, [beforeRecover]);

  // Re-runs after each recovery remount (recoverKey changes) so the observer and
  // listeners re-attach to the FRESH scroll element (the keyed remount replaces
  // the node). Refs attach before effects, so scrollRef.current is already the
  // new node here.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let raf = 0;
    let timer = 0;

    const check = () => {
      const node = scrollRef.current;
      const bounds = boundsRef.current;
      if (!node || !bounds) return;
      // Broken: the scroll container's bottom edge spilled past its fixed
      // parent's bottom (it grew to content height instead of its flex slot)
      // AND it has no scrollable overflow. Parent-relative, so it does not
      // depend on the header/search/filter chrome or safe-area heights. A
      // healthy short list has no overflow but does not spill; a healthy tall
      // list spills nothing and has overflow. Both are excluded.
      const spill =
        node.getBoundingClientRect().bottom - bounds.getBoundingClientRect().bottom;
      const notScrollable = node.scrollHeight - node.clientHeight < 4;
      if (!(spill > 4 && notScrollable)) return;
      const now = performance.now();
      if (now - lastRecoverRef.current < 500) return; // debounce: never loop
      lastRecoverRef.current = now;
      beforeRecoverRef.current?.();
      setRecoverKey((k) => k + 1);
    };

    // iOS settles the broken layout slightly after the triggering event, so
    // probe next frame and again shortly after. schedule() coalesces.
    const schedule = () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      raf = requestAnimationFrame(check);
      timer = window.setTimeout(check, 200);
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') schedule();
    };

    schedule(); // initial probe (also catches a cold-mount break)

    // The observer fires on every re-layout that changes either element's size,
    // which is exactly when the break can occur. The callback only schedules a
    // cheap bounding-rect probe; a remount happens only when broken, so there is
    // no observer loop (post-remount size is healthy + stable, and the 500ms
    // debounce backstops a burst).
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    if (boundsRef.current) ro.observe(boundsRef.current);
    window.addEventListener('online', schedule);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
      ro.disconnect();
      window.removeEventListener('online', schedule);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [recoverKey, scrollRef, boundsRef]);

  return recoverKey;
}
